import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type FieldHelpContent = {
  title: string;
  summary: string;
  example?: string;
  detail?: string;
  useCases?: string[];
};

/**
 * Sparse “?” help — short tip → Show more (plan/46).
 * Desk: anchored popover. Phone-friendly tap (not hover-only).
 */
export function FieldHelp({ content }: { content: FieldHelpContent }) {
  const tipId = useId();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const hasMore =
    Boolean(content.detail?.trim()) ||
    Boolean(content.useCases && content.useCases.length > 0);

  function openHelp() {
    const el = btnRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const width = 288;
      setPos({
        top: rect.bottom + 6,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      });
    }
    setExpanded(false);
    setOpen(true);
  }

  function closeHelp() {
    setOpen(false);
    setExpanded(false);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeHelp();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] text-[10px] font-semibold leading-none text-[var(--color-muted)] hover:border-[var(--color-fg)]/40 hover:text-[var(--color-fg)]"
        aria-label={`About ${content.title}`}
        aria-expanded={open}
        aria-controls={open ? tipId : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (open) closeHelp();
          else openHelp();
        }}
      >
        ?
      </button>
      {open &&
        pos &&
        createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[90] cursor-default bg-transparent"
              aria-label="Dismiss help"
              onClick={closeHelp}
            />
            <div
              id={tipId}
              role="dialog"
              aria-label={content.title}
              className="fixed z-[91] w-72 max-w-[calc(100vw-1rem)] rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-left shadow-[var(--shadow-float)]"
              style={{ top: pos.top, left: pos.left }}
            >
              <div className="font-[family-name:var(--font-display)] text-sm font-semibold tracking-tight">
                {content.title}
              </div>
              <p className="mt-1.5 m-0 text-xs leading-relaxed text-[var(--color-fg)]">
                {content.summary}
              </p>
              {content.example ? (
                <p className="mt-2 m-0 text-xs leading-relaxed text-[var(--color-muted)]">
                  <span className="font-medium text-[var(--color-fg)]">
                    Example:{" "}
                  </span>
                  {content.example}
                </p>
              ) : null}
              {expanded && hasMore ? (
                <div className="mt-2 space-y-2 border-t border-[var(--color-border)]/80 pt-2 text-xs leading-relaxed text-[var(--color-muted)]">
                  {content.detail ? <p className="m-0">{content.detail}</p> : null}
                  {content.useCases && content.useCases.length > 0 ? (
                    <ul className="m-0 list-disc space-y-1 pl-4">
                      {content.useCases.map((u) => (
                        <li key={u}>
                          <span className="font-medium text-[var(--color-fg)]">
                            Use when:{" "}
                          </span>
                          {u}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
              {hasMore && !expanded ? (
                <button
                  type="button"
                  className="mt-2 text-xs font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                  onClick={() => setExpanded(true)}
                >
                  Show more
                </button>
              ) : null}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
